import React from "react";
import { Switch, Route } from "react-router-dom";
import { PortalLayout } from "../../components/PortalLayout/PortalLayout";
import { useActiveOrganization } from "../../hooks/useActiveOrganization";
import {
  ViewerViewer,
  useRequesterPortalView,
  ApprovalWorkflowStatusEnum,
  UserRoleEnum,
  PurchaseRequestStatusEnum,
} from "../../schema";
import { ErrorView } from "../ErrorView/ErrorView";
import { LoadingView } from "../LoadingView/LoadingView";
import { WithoutOrganizationView } from "../WithoutOrganizationView/WithoutOrganizationView";
import { CreateRequestFromPortalView } from "./CreateRequestFromPortalView";
import { DetailRequestFromPortalView } from "./DetailRequestFromPortalView";
import { EditRequestFromPortalView } from "./EditRequestFromPortalView";
import { ListRequestFromPortalView } from "./ListRequestFromPortalView";

interface RequesterPortalViewProps {
  viewer: ViewerViewer;
}

export const RequesterPortalView: React.FC<RequesterPortalViewProps> = ({
  viewer,
}) => {
  const organization = useActiveOrganization(viewer.organizations);

  const { data, loading, error } = useRequesterPortalView({
    variables: {
      approvalWorkflowStatuses: [
        ApprovalWorkflowStatusEnum.IN_PROGRESS,
        ApprovalWorkflowStatusEnum.APPROVED,
        ApprovalWorkflowStatusEnum.DECLINED,
      ],
      organizationId: organization?.id ?? "",
      filter: {
        userId: viewer?.id || null,
        projectId: null,
        supplierId: null,
        departmentId: null,
        dateRange: null,
        status: null,
      },
    },
  });

  if (error !== undefined) {
    return <ErrorView error={error}></ErrorView>;
  }

  if (loading || !data?.viewer) {
    return <LoadingView overlay></LoadingView>;
  }

  if (!organization) {
    return <WithoutOrganizationView viewer={viewer} />;
  }

  const hasKanbanAccess = viewer.roles.some((role) =>
    [UserRoleEnum.ADMIN, UserRoleEnum.KEY_USER, UserRoleEnum.BUYER].includes(
      role,
    ),
  );

  const approvalWorkflows = data.viewer?.approvalWorkflowsByStatus;
  const purchaseRequests = data.viewer?.organization.purchaseRequests;
  const department = data.viewer?.department;
  const project = data.viewer?.project;
  const costCentre = data.viewer?.costCentre;

  const inProgressApprovalsRequests: number =
    approvalWorkflows?.filter(
      (i) => i.status === ApprovalWorkflowStatusEnum.IN_PROGRESS,
    ).length || 0;

  const inProgressPurchaseRequests: number =
    purchaseRequests?.filter(
      (i) =>
        i.status === PurchaseRequestStatusEnum.READY &&
        !i.latestApprovalWorkflow,
    ).length || 0;

  return (
    <PortalLayout
      hasKanbanAccess={hasKanbanAccess}
      inProgressApprovalsRequests={inProgressApprovalsRequests}
      inProgressPurchaseRequests={inProgressPurchaseRequests}
      organization={organization}
      viewer={viewer}
    >
      <Switch>
        <Route exact path={`/:activeOrganization/purchase-requests/create`}>
          <CreateRequestFromPortalView
            organization={data.viewer.organization}
            viewer={viewer}
            department={department}
            project={project}
            costCentre={costCentre}
          />
        </Route>
        <Route
          exact
          path={`/:activeOrganization/purchase-requests/:purchaseRequestId`}
        >
          <DetailRequestFromPortalView
            organization={data.viewer.organization}
            viewer={viewer}
          />
        </Route>
        <Route
          exact
          path={`/:activeOrganization/purchase-requests/:purchaseRequestId/edit`}
        >
          <EditRequestFromPortalView
            organization={data.viewer.organization}
            viewer={viewer}
          />
        </Route>
        <Route exact path={`/:activeOrganization/purchase-requests`}>
          <ListRequestFromPortalView
            organization={data.viewer.organization}
            purchaseRequests={purchaseRequests}
          />
        </Route>
      </Switch>
    </PortalLayout>
  );
};
